import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuFileSpreadsheet, LuUsers,
} from 'react-icons/lu';
import { ENROLLMENT_STATUS, SEMAFORO, Badge, errorMessage, fmtDate, fmtScore } from './trainingUtils';
import {
  COMPANY_BRAND, BRAND_NAVY_ARGB, SEMAFORO_ARGB, SEMAFORO_TINT_ARGB, getLogoBase64,
} from '../utils/brandAssets';

const STATUS_XLSX = {
  approved: { label: 'Aprovado', color: 'verde' },
  failed: { label: 'Reprovado', color: 'vermelho' },
  in_progress: { label: 'Em andamento', color: null },
};

/** Lista de alunos do relatório → planilha (exceljs, mesmo padrão visual dos exports do sistema). */
async function exportStudentsXlsx(report) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = COMPANY_BRAND;
  wb.created = new Date();
  const ws = wb.addWorksheet('Alunos');

  let r = 1;
  const logo = await getLogoBase64();
  if (logo) {
    ws.addImage(wb.addImage({ base64: logo, extension: 'jpeg' }), { tl: { col: 0, row: 0 }, ext: { width: 62, height: 62 } });
    r = 5;
  }
  ws.getCell(r, 1).value = `Relatório do curso: ${report.course.title}`;
  ws.getCell(r, 1).font = { bold: true, size: 14, color: { argb: BRAND_NAVY_ARGB } };
  r += 1;
  const s = report.summary;
  ws.getCell(r, 1).value = `Matriculados: ${s.enrolled} · Concluídos: ${s.finished} (${s.finished_percent}%) · Aprovados: ${s.approved} · Reprovados: ${s.failed} · `
    + `Nota média: ${s.average_score ?? '—'} · Taxa de aprovação: ${s.approval_rate ?? '—'}%`;
  r += 1;
  ws.getCell(r, 1).value = `Gerado em ${new Date().toLocaleString('pt-BR')}`;
  ws.getCell(r, 1).font = { italic: true, color: { argb: 'FF9AA3AF' } };
  r += 2;

  const headers = ['Aluno', 'Usuário', 'Matrícula', 'Empresa', 'Departamento', 'Status', 'Progresso (%)', 'Nota final',
    ...report.assessments.flatMap((a) => [`${a.title} — melhor nota`, `${a.title} — tentativas`]),
    'Total de tentativas', 'Início', 'Conclusão', 'Prazo', 'Atrasado'];
  headers.forEach((h, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_ARGB } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  ws.getRow(r).height = 30;
  r += 1;

  report.students.forEach((st) => {
    const status = STATUS_XLSX[st.status] || STATUS_XLSX.in_progress;
    const row = [
      st.name, st.username, st.registration, st.company, st.department, status.label, st.progress_percent, st.final_score,
      ...st.assessments.flatMap((a) => [a.best_score, a.max_attempts ? `${a.attempts_used}/${a.max_attempts}` : a.attempts_used]),
      st.total_attempts,
      st.started_at ? new Date(st.started_at) : null,
      st.completed_at ? new Date(st.completed_at) : null,
      st.due_date ? new Date(`${st.due_date}T12:00:00`) : null,
      st.overdue ? 'Sim' : 'Não',
    ];
    row.forEach((v, i) => {
      const cell = ws.getCell(r, i + 1);
      cell.value = v ?? '';
      if (v instanceof Date) cell.numFmt = 'dd/mm/yyyy';
    });
    if (status.color) {
      const cell = ws.getCell(r, 6);
      cell.font = { bold: true, color: { argb: SEMAFORO_ARGB[status.color] } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB[status.color] } };
    }
    r += 1;
  });

  headers.forEach((h, i) => { ws.getColumn(i + 1).width = Math.min(Math.max(h.length + 2, 12), 32); });
  ws.getColumn(1).width = 30;

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `treinamento-${report.course.title.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-').toLowerCase() || report.course.id}-alunos.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Kpi({ label, children }) {
  return (
    <div className="training-kpi">
      <div className="training-kpi-label">{label}</div>
      <div className="training-kpi-value">{children}</div>
    </div>
  );
}

export default function TrainingReportTab({ course }) {
  const [report, setReport] = useState(null);
  const [companyId, setCompanyId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/training/courses/${course.id}/report`, {
        params: { company_id: companyId || undefined, department_id: departmentId || undefined },
      });
      setReport(data);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar o relatório'));
    }
  }, [course.id, companyId, departmentId]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try { await exportStudentsXlsx(report); } catch (e) { setError('Não foi possível gerar a planilha.'); } finally { setExporting(false); }
  };

  if (!report) return <div className="training-hint">{error || 'Carregando…'}</div>;

  const s = report.summary;
  const sem = SEMAFORO[s.approval_semaphore];
  const departments = report.filter_options.departments.filter((d) => !companyId || d.company_id === Number(companyId));

  return (
    <div>
      {error && <div className="alert-custom danger">{error}</div>}
      <div className="training-toolbar">
        <select className="form-control" value={companyId} onChange={(e) => { setCompanyId(e.target.value); setDepartmentId(''); }}>
          <option value="">Todas as empresas</option>
          {report.filter_options.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-control" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
          <option value="">Todos os departamentos</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button type="button" className="training-btn" onClick={handleExport} disabled={exporting || !report.students.length} style={{ marginLeft: 'auto' }}>
          <LuFileSpreadsheet color="var(--success)" /> {exporting ? 'Gerando…' : 'Exportar planilha'}
        </button>
      </div>

      <div className="training-kpis">
        <Kpi label="Matriculados">{s.enrolled}</Kpi>
        <Kpi label="Concluídos">{s.finished} <span style={{ fontSize: 13, color: 'var(--text-3)' }}>({s.finished_percent}%)</span></Kpi>
        <Kpi label="Aprovados">{s.approved}</Kpi>
        <Kpi label="Reprovados">{s.failed}</Kpi>
        <Kpi label="Nota média">{fmtScore(s.average_score)}</Kpi>
        <Kpi label="Taxa de aprovação">
          {s.approval_rate === null ? '—' : `${fmtScore(s.approval_rate)}%`}
          {sem && <span className="training-badge" style={{ background: sem.bg, color: sem.color }}>● {sem.label}</span>}
        </Kpi>
      </div>
      <div className="training-hint" style={{ marginTop: -8, marginBottom: 16 }}>
        Concluídos = aprovados + reprovados. Taxa de aprovação = aprovados ÷ concluídos (verde ≥ 80%, amarelo 60–79%, vermelho &lt; 60%).
        {s.overdue > 0 && <> · <strong style={{ color: 'var(--danger)' }}>{s.overdue} aluno{s.overdue > 1 ? 's' : ''} com prazo vencido</strong></>}
      </div>

      {!report.students.length ? (
        <div className="empty-state"><LuUsers size={32} color="var(--text-3)" /><p>Nenhum aluno matriculado{companyId || departmentId ? ' com esses filtros' : ' ainda'}</p></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="training-table">
            <thead>
              <tr>
                <th>Aluno</th><th>Empresa / Depto.</th><th>Status</th><th>Progresso</th><th>Nota final</th>
                {report.assessments.map((a) => <th key={a.id}>{a.title}</th>)}
                <th>Prazo</th>
              </tr>
            </thead>
            <tbody>
              {report.students.map((st) => (
                <tr key={st.enrollment_id}>
                  <td><div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{st.name}</div><div className="training-hint" style={{ marginTop: 0 }}>{st.username}</div></td>
                  <td>{st.company}<div className="training-hint" style={{ marginTop: 0 }}>{st.department || '—'}</div></td>
                  <td><Badge meta={ENROLLMENT_STATUS[st.status]} /></td>
                  <td>
                    <div className="training-progress"><span style={{ width: `${st.progress_percent}%` }} /></div>
                    <div className="training-hint" style={{ marginTop: 2 }}>{st.progress_percent}%</div>
                  </td>
                  <td style={{ fontWeight: 700 }}>{fmtScore(st.final_score)}</td>
                  {st.assessments.map((a) => (
                    <td key={a.assessment_id}>
                      <span style={{ fontWeight: 600, color: a.passed ? 'var(--success)' : a.attempts_used ? 'var(--danger)' : 'var(--text-3)' }}>{fmtScore(a.best_score)}</span>
                      <div className="training-hint" style={{ marginTop: 0 }}>
                        {a.attempts_used}{a.max_attempts ? `/${a.max_attempts}` : ''} tentativa{a.attempts_used !== 1 ? 's' : ''}
                      </div>
                    </td>
                  ))}
                  <td style={{ color: st.overdue ? 'var(--danger)' : undefined, fontWeight: st.overdue ? 700 : 400 }}>
                    {fmtDate(st.due_date)}{st.overdue ? ' (vencido)' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
